"use client";

import type { Settings } from "@types";
import Image from "next/image";
import Link from "next/link";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import Button from "@/components/inputs/Button";
import { urlFor } from "@/sanity/lib/image";
import { typography } from "@/styles/design-tokens";

type HeaderType = Settings["header"] & {
  locked: boolean;
};

export default function Header(props: HeaderType) {
  const { logo, mainCTA, menuList, locked } = props ?? {};
  const [open, setOpen] = useState(false);
  const logoUrl = logo ? urlFor(logo).url() : "/images/header-logo.webp";
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && menuRef.current) {
      menuRef.current.focus();
    }
  }, [open]);

  const handleCloseMenu = () => {
    setOpen(false);
  };

  return (
    <>
      <header className="py-4 w-full fixed top-0 left-0 z-50 pointer-events-none">
        <div className="px-4 w-full flex justify-between items-center max-w-[1728px] mx-auto sm:px-6">
          <Link href="/" className="!pointer-events-auto">
            <Image
              src={logoUrl}
              width={200}
              height={120}
              alt="atlanime festival logo"
              priority
              className="object-fit h-20 w-auto sm:h-40"
            />
          </Link>
          <div className="flex items-center gap-6">
            {mainCTA && <Button {...mainCTA} classes="hidden sm:flex" />}
            {!locked && (
              <MenuButton
                open={open}
                setOpen={setOpen}
                ariaControls="mobile-menu"
              />
            )}
          </div>
        </div>
      </header>

      {/* Sliding menu */}
      {menuList && menuList.length > 0 && (
        <div
          id="mobile-menu"
          ref={menuRef}
          tabIndex={-1}
          aria-hidden={!open}
          className={`
              fixed bottom-0 right-0 w-full 
              transform transition-transform duration-300 ease-in-out
              p-4 flex items-end justify-end z-50
              ${open ? "" : "translate-y-[200%]"}
              
            `}
          role="menu"
        >
          <div
            className="max-w-64 w-full bg-white shadow-lg rounded-l-xl flex flex-col border-2 rounded-xl overflow-hidden outline-none"
            id="mobile-menu"
            ref={menuRef}
            tabIndex={-1}
            aria-hidden={!open}
            onClick={handleCloseMenu}
          >
            {menuList.map(
              (item: NonNullable<typeof menuList>[number], i: number) => (
                <Button
                  key={item._key}
                  {...item}
                  classes={`${typography.caption} ${
                    i !== 0 ? "border-t border-dashed" : ""
                  } py-4 px-6 hover:bg-yellow`}
                />
              ),
            )}
          </div>
        </div>
      )}
    </>
  );
}

// MenuButton component extracted here
type MenuButtonProps = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  ariaControls?: string;
};

function MenuButton({ open, setOpen, ariaControls }: MenuButtonProps) {
  return (
    <button
      type="button"
      aria-label="Toggle menu"
      aria-expanded={open}
      aria-controls={ariaControls}
      onClick={() => setOpen((prev) => !prev)}
      className="cursor-pointer !pointer-events-auto"
    >
      <svg
        width="64"
        height="48"
        viewBox="0 0 80 64"
        fill="none"
        aria-hidden="true"
      >
        <title>Menu toggle icon</title>
        <g>
          <rect
            x="1"
            y="1"
            width="78"
            height="14"
            rx="7"
            fill="#635FCD"
            stroke="#403BBE"
            strokeWidth="2"
            style={{
              transition: "all 0.3s",
              transformOrigin: "40px 8px",
              transform: open ? "rotate(45deg) translate(12px,22px)" : "none",
            }}
          />
          <rect
            x="1"
            y="25"
            width="78"
            height="14"
            rx="7"
            fill="#635FCD"
            stroke="#403BBE"
            strokeWidth="2"
            style={{
              transition: "all 0.3s",
              opacity: open ? 0 : 1,
            }}
          />
          <rect
            x="1"
            y="49"
            width="78"
            height="14"
            rx="7"
            fill="#635FCD"
            stroke="#403BBE"
            strokeWidth="2"
            style={{
              transition: "all 0.3s",
              transformOrigin: "40px 56px",
              transform: open ? "rotate(-45deg) translate(10px,-22px)" : "none",
            }}
          />
        </g>
      </svg>
    </button>
  );
}
